/**
 * Hook para gerenciar seleção em massa de itens em listas
 * Responsabilidade: Controlar estado de seleção múltipla
 */

import { useState, useCallback, useMemo } from 'react';

export interface UseBulkSelectionReturn<T> {
    selectedIds: Set<string>;
    isSelected: (id: string) => boolean;
    isAllSelected: boolean;
    isSomeSelected: boolean;
    toggleSelection: (id: string) => void;
    toggleAll: () => void;
    clearSelection: () => void;
    getSelectedItems: () => T[];
}

/**
 * @param items Universo para resolver itens selecionados (export, delete em massa).
 * @param scopeItems Escopo do "selecionar todos" do header — ex.: página atual. Default: items.
 */
export function useBulkSelection<T extends { id: string }>(
    items: T[],
    scopeItems?: T[],
): UseBulkSelectionReturn<T> {
    const scope = scopeItems ?? items;
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    const isSelected = useCallback(
        (id: string) => selectedIds.has(id),
        [selectedIds]
    );

    const isAllSelected = useMemo(
        () => scope.length > 0 && scope.every(item => selectedIds.has(item.id)),
        [scope, selectedIds],
    );

    const isSomeSelected = useMemo(() => {
        const selectedOnScope = scope.filter(item => selectedIds.has(item.id)).length;
        return selectedOnScope > 0 && selectedOnScope < scope.length;
    }, [scope, selectedIds]);

    const toggleSelection = useCallback((id: string) => {
        setSelectedIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    }, []);

    const toggleAll = useCallback(() => {
        setSelectedIds(prev => {
            const allScopeSelected =
                scope.length > 0 && scope.every(item => prev.has(item.id));
            const next = new Set(prev);
            if (allScopeSelected) {
                scope.forEach(item => next.delete(item.id));
            } else {
                scope.forEach(item => next.add(item.id));
            }
            return next;
        });
    }, [scope]);

    const clearSelection = useCallback(() => {
        setSelectedIds(new Set());
    }, []);

    const getSelectedItems = useCallback(() => {
        return items.filter(item => selectedIds.has(item.id));
    }, [items, selectedIds]);

    return {
        selectedIds,
        isSelected,
        isAllSelected,
        isSomeSelected,
        toggleSelection,
        toggleAll,
        clearSelection,
        getSelectedItems
    };
}
